/*
 * Copyright © HatioLab Inc. All rights reserved.
 *
 * Stocker 3D — aisle, rail, crane, ports, enclosure, rack frames, cell stocks.
 */

import * as THREE from 'three'
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { RealObjectGroup } from '@hatiolab/things-scene'
import type { Stocker, StockerData, CraneData } from './stocker.js'
import { computeLayout } from './stocker.js'

const FRAME_COLOR = 0x8a8a8a
const BOARD_COLOR = 0xcccccc
const AISLE_COLOR = 0xd4d4c8
const RAIL_COLOR = 0x666666
const CRANE_COLOR = 0xff6600
const CRANE_MAST_COLOR = 0xcc5500
const FORK_COLOR = 0xddaa00
const CELL_EMPTY_COLOR = 0xf0f0f0
const CELL_FULL_COLOR = 0x4a9eff
const CELL_RESERVED_COLOR = 0xffcc00
const CELL_ERROR_COLOR = 0xe74c3c

function cellColor3d(status?: string): number {
  switch (status) {
    case 'FULL': return CELL_FULL_COLOR
    case 'RESERVED': return CELL_RESERVED_COLOR
    case 'ERROR': return CELL_ERROR_COLOR
    default: return CELL_EMPTY_COLOR
  }
}

interface CraneState {
  x: number; y: number; forkZ: number
  targetX: number; targetY: number; targetForkZ: number
  bayWidth: number; levelHeight: number; maxBays: number; levels: number
  leftRackDepth: number; rightRackDepth: number; aisleDepth: number
}

interface CraneInstance {
  mastGroup: THREE.Group
  carriageGroup: THREE.Group
  forkGroup: THREE.Group
  state: CraneState
}

export class Stocker3d extends RealObjectGroup {
  private _cranes: CraneInstance[] = []
  private _cellMeshes: THREE.Mesh[] = []
  private _animRaf = 0

  get stocker(): Stocker {
    return this.component as unknown as Stocker
  }

  // bottom origin: object3d가 zPos(바닥)에 위치 — 내부 mesh는 y=0부터 위로 쌓임
  protected get syncZPosOffset(): number {
    return 0
  }

  override get geometricOffsetY(): number {
    return 0
  }

  build() {
    super.build()

    const state = this.component.state
    const { width = 200, height = 100, depth: totalHeight = 80, rotation = 0, aisleRatio = 0.15 } = state

    const layout = computeLayout(state)
    let orientY = -rotation
    if (layout.vertical) orientY += Math.PI / 2
    if (layout.flipped) orientY += Math.PI
    this.object3d.rotation.y = orientY

    const alongSize = layout.along
    const acrossSize = layout.across
    const aisleDepth = acrossSize * aisleRatio
    const rackAreaDepth = acrossSize - aisleDepth
    const leftRackDepth = layout.leftRack ? rackAreaDepth / (layout.rightRack ? 2 : 1) : 0
    const rightRackDepth = layout.rightRack ? rackAreaDepth / (layout.leftRack ? 2 : 1) : 0

    const frameGeoms: THREE.BufferGeometry[] = []
    const boardGeoms: THREE.BufferGeometry[] = []

    // ── Rack sides ──
    if (layout.leftRack) {
      const rackZ = -(aisleDepth / 2 + leftRackDepth / 2)
      this._buildRackSide('L', layout.leftRack.config, alongSize, totalHeight, leftRackDepth, rackZ, frameGeoms, boardGeoms)
    }
    if (layout.rightRack) {
      const rackZ = aisleDepth / 2 + rightRackDepth / 2
      this._buildRackSide('R', layout.rightRack.config, alongSize, totalHeight, rightRackDepth, rackZ, frameGeoms, boardGeoms)
    }

    // merge frames
    if (!state.hideRackFrame && frameGeoms.length > 0) {
      const mat = new THREE.MeshStandardMaterial({ color: FRAME_COLOR, metalness: 0.85, roughness: 0.35, transparent: true, opacity: 0.8 })
      const mesh = new THREE.Mesh(BufferGeometryUtils.mergeGeometries(frameGeoms), mat)
      mesh.castShadow = true
      this.object3d.add(mesh)
    }

    if (!state.hideRackFrame && boardGeoms.length > 0) {
      const mat = new THREE.MeshStandardMaterial({ color: BOARD_COLOR, side: THREE.DoubleSide, transparent: true, opacity: 0.5 })
      this.object3d.add(new THREE.Mesh(BufferGeometryUtils.mergeGeometries(boardGeoms), mat))
    }

    // ── Enclosure ──
    const { fillStyle } = state
    if (fillStyle && fillStyle !== 'transparent' && fillStyle !== 'rgba(0,0,0,0)') {
      this._buildEnclosure(alongSize, acrossSize, totalHeight, fillStyle)
    }

    // ── Aisle ──
    const aisleGeom = new THREE.PlaneGeometry(alongSize, aisleDepth)
    aisleGeom.rotateX(-Math.PI / 2)
    const aisleMesh = new THREE.Mesh(aisleGeom, new THREE.MeshStandardMaterial({ color: AISLE_COLOR, roughness: 0.9 }))
    aisleMesh.position.y = 0.1
    aisleMesh.receiveShadow = true
    this.object3d.add(aisleMesh)

    // rail
    const railMesh = new THREE.Mesh(
      new THREE.BoxGeometry(alongSize, 1, 2),
      new THREE.MeshStandardMaterial({ color: RAIL_COLOR, metalness: 0.8, roughness: 0.3 })
    )
    railMesh.position.set(0, 0.5, 0)
    this.object3d.add(railMesh)

    // ── Cranes ──
    const maxBays = layout.maxBays
    const levels = Math.max(state.lLevels || 0, state.rLevels || 0, 1)
    this._buildCranes(alongSize, totalHeight, maxBays, levels, aisleDepth, leftRackDepth, rightRackDepth)

  }

  private _buildRackSide(
    side: 'L' | 'R', config: { bays: number; levels: number; depthCount: number },
    totalWidth: number, totalHeight: number, rackDepth: number, rackZ: number,
    frameGeoms: THREE.BufferGeometry[], boardGeoms: THREE.BufferGeometry[]
  ) {
    const { bays, levels, depthCount } = config
    const bayWidth = totalWidth / bays
    const levelHeight = totalHeight / levels
    const cellDepthSize = rackDepth / depthCount
    const postW = Math.min(bayWidth * 0.05, 2)
    const inset = 3

    const hw = totalWidth / 2 - inset
    const hd = rackDepth / 2 - inset

    // vertical posts — 수평바 + 1/2 두께로 접합부 갭 채움
    const topRailY = totalHeight - inset
    const bottomRailY = inset
    const postHeight = topRailY - bottomRailY + postW
    const postGeom = new THREE.BoxGeometry(postW, postHeight, postW)
    for (const [px, pz] of [[-hw, rackZ - hd], [-hw, rackZ + hd], [hw, rackZ - hd], [hw, rackZ + hd]]) {
      const g = postGeom.clone()
      g.translate(px, bottomRailY - postW / 2 + postHeight / 2, pz)
      frameGeoms.push(g)
    }

    // horizontal rails — 좌우 inset, 상하는 enclosure 안쪽에 위치
    const railW = totalWidth - inset * 2
    const railAlongGeom = new THREE.BoxGeometry(railW, postW, postW)
    const railDepthW = rackDepth - inset * 2
    const railDepthGeom = new THREE.BoxGeometry(postW, postW, railDepthW)

    for (let l = 0; l <= levels; l++) {
      let y = l * levelHeight
      // bottom은 enclosure 바닥에서 올라오고, top은 천장에서 내려옴
      if (l === 0) y = inset
      else if (l === levels) y = totalHeight - inset

      // along 방향 (전면/후면)
      for (const dz of [rackZ - hd, rackZ + hd]) {
        const g = railAlongGeom.clone()
        g.translate(0, y, dz)
        frameGeoms.push(g)
      }
      // depth 방향 (좌측/우측 끝)
      for (const px of [-hw, hw]) {
        const g = railDepthGeom.clone()
        g.translate(px, y, rackZ)
        frameGeoms.push(g)
      }
    }

    // shelf boards
    const boardW = totalWidth - inset * 2
    const boardD = rackDepth - inset * 2
    for (let l = 0; l < levels; l++) {
      const y = l * levelHeight + 0.5
      const g = new THREE.PlaneGeometry(boardW, boardD)
      g.rotateX(-Math.PI / 2)
      g.translate(0, y, rackZ)
      boardGeoms.push(g)
    }

    // cell stock
    for (let l = 1; l <= levels; l++) {
      for (let b = 1; b <= bays; b++) {
        for (let d = 1; d <= depthCount; d++) {
          const status = this.stocker.getCellStatus(side, b, l, d)
          const geom = new THREE.BoxGeometry(bayWidth * 0.7, levelHeight * 0.7, cellDepthSize * 0.7)
          const mat = new THREE.MeshStandardMaterial({
            color: cellColor3d(status), metalness: 0.1, roughness: 0.8,
            transparent: !status || status === 'EMPTY',
            opacity: (!status || status === 'EMPTY') ? 0.15 : 1
          })
          const mesh = new THREE.Mesh(geom, mat)
          mesh.position.set(
            -totalWidth / 2 + (b - 0.5) * bayWidth,
            (l - 0.5) * levelHeight,
            rackZ + (d - 0.5 - depthCount / 2) * cellDepthSize
          )
          mesh.name = this.stocker.getLocationId(side, b, l, d)
          this._cellMeshes.push(mesh)
          this.object3d.add(mesh)
        }
      }
    }
  }

  private _buildEnclosure(alongSize: number, acrossSize: number, totalHeight: number, fillStyle: string) {
    let color = 0xcccccc
    let opacity = 0.4
    // rgba에서 alpha 추출, 색상만 THREE.Color로
    const rgbaMatch = fillStyle.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/)
    if (rgbaMatch) {
      const [, r, g, b, a] = rgbaMatch
      color = new THREE.Color(parseInt(r) / 255, parseInt(g) / 255, parseInt(b) / 255).getHex()
      if (a !== undefined) opacity = parseFloat(a)
    } else {
      try { color = new THREE.Color(fillStyle).getHex() } catch {}
    }
    const mat = new THREE.MeshStandardMaterial({ color, transparent: true, opacity, side: THREE.DoubleSide, depthWrite: false })
    const halfW = alongSize / 2
    const halfD = acrossSize / 2

    for (const [w, h, x, y, z, ry] of [
      [alongSize, totalHeight, 0, totalHeight / 2, -halfD, 0],
      [alongSize, totalHeight, 0, totalHeight / 2, halfD, 0],
      [acrossSize, totalHeight, -halfW, totalHeight / 2, 0, Math.PI / 2],
      [acrossSize, totalHeight, halfW, totalHeight / 2, 0, Math.PI / 2]
    ] as [number, number, number, number, number, number][]) {
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat)
      mesh.position.set(x, y, z)
      mesh.rotation.y = ry
      this.object3d.add(mesh)
    }
    const roofGeom = new THREE.PlaneGeometry(alongSize, acrossSize)
    roofGeom.rotateX(-Math.PI / 2)
    const roofMesh = new THREE.Mesh(roofGeom, mat)
    roofMesh.position.y = totalHeight
    this.object3d.add(roofMesh)
  }

  private _buildCranes(alongSize: number, totalHeight: number, maxBays: number, levels: number, aisleDepth: number, leftRackDepth: number, rightRackDepth: number) {
    const cranesData = this.stocker.cranesData
    const craneCount = Math.max(cranesData.length, this.component.state.cranes || 1)
    const bayWidth = alongSize / maxBays
    const levelHeight = totalHeight / levels
    const craneHeight = totalHeight * 0.95

    const mastMat = new THREE.MeshStandardMaterial({ color: CRANE_MAST_COLOR, metalness: 0.7, roughness: 0.4 })
    const carriageMat = new THREE.MeshStandardMaterial({ color: CRANE_COLOR, metalness: 0.3, roughness: 0.6 })
    const forkMat = new THREE.MeshStandardMaterial({ color: FORK_COLOR, metalness: 0.5, roughness: 0.5 })

    for (let i = 0; i < craneCount; i++) {
      const cd = cranesData[i]
      const bay = cd?.bay ?? Math.round((i + 1) * maxBays / (craneCount + 1))
      const level = cd?.level ?? 1
      const side = cd?.side
      const status = cd?.status || 'IDLE'

      const craneX = -alongSize / 2 + (bay - 0.5) * bayWidth
      const craneY = (level - 0.5) * levelHeight
      let targetForkZ = 0
      if ((status === 'PICKING' || status === 'PLACING') && side) {
        targetForkZ = side === 'L' ? -(aisleDepth / 2 + leftRackDepth / 2) : (aisleDepth / 2 + rightRackDepth / 2)
      }

      const mastGroup = new THREE.Group()
      mastGroup.position.x = craneX
      this.object3d.add(mastGroup)

      const mastMesh = new THREE.Mesh(new THREE.BoxGeometry(3, craneHeight, 3), mastMat)
      mastMesh.position.y = craneHeight / 2
      mastMesh.castShadow = true
      mastGroup.add(mastMesh)

      const topBeam = new THREE.Mesh(new THREE.BoxGeometry(bayWidth * 0.3, 2, aisleDepth * 0.8), mastMat)
      topBeam.position.y = craneHeight - 1
      mastGroup.add(topBeam)

      const carriageGroup = new THREE.Group()
      carriageGroup.position.y = craneY
      mastGroup.add(carriageGroup)

      const carrW = bayWidth * 0.7, carrH = 4, carrD = aisleDepth * 0.5
      const carriageMesh = new THREE.Mesh(new THREE.BoxGeometry(carrW, carrH, carrD), carriageMat)
      carriageMesh.castShadow = true
      carriageGroup.add(carriageMesh)

      const forkGroup = new THREE.Group()
      carriageGroup.add(forkGroup)

      const forkLength = Math.max(leftRackDepth, rightRackDepth) * 0.8
      const forkW = carrW * 0.15, forkH = 1.5
      const prongGeom = new THREE.BoxGeometry(forkW, forkH, forkLength)

      const lp = new THREE.Mesh(prongGeom, forkMat)
      lp.position.set(-carrW * 0.25, -carrH / 2 + forkH / 2, 0)
      forkGroup.add(lp)
      const rp = new THREE.Mesh(prongGeom.clone(), forkMat)
      rp.position.set(carrW * 0.25, -carrH / 2 + forkH / 2, 0)
      forkGroup.add(rp)
      const baseMesh = new THREE.Mesh(new THREE.BoxGeometry(carrW * 0.7, forkH, forkW), forkMat)
      baseMesh.position.set(0, -carrH / 2 + forkH / 2, -forkLength / 2 + forkW / 2)
      forkGroup.add(baseMesh)

      this._cranes.push({
        mastGroup, carriageGroup, forkGroup,
        state: { x: craneX, y: craneY, forkZ: 0, targetX: craneX, targetY: craneY, targetForkZ, bayWidth, levelHeight, maxBays, levels, leftRackDepth, rightRackDepth, aisleDepth }
      })
    }
    if (this._cranes.some(c => c.state.targetForkZ !== 0)) this._startCraneAnim()
  }

  updateCraneTargets() {
    const cranesData = this.stocker.cranesData
    const alongSize = this.stocker.layout.along
    for (let i = 0; i < this._cranes.length; i++) {
      const crane = this._cranes[i]
      const cd = cranesData[i]
      const s = crane.state
      const bay = cd?.bay ?? Math.round((i + 1) * s.maxBays / (this._cranes.length + 1))
      const level = cd?.level ?? 1
      const side = cd?.side
      const status = cd?.status || 'IDLE'
      s.targetX = -alongSize / 2 + (bay - 0.5) * s.bayWidth
      s.targetY = (level - 0.5) * s.levelHeight
      if ((status === 'PICKING' || status === 'PLACING') && side) {
        s.targetForkZ = side === 'L' ? -(s.aisleDepth / 2 + s.leftRackDepth / 2) : (s.aisleDepth / 2 + s.rightRackDepth / 2)
      } else { s.targetForkZ = 0 }
    }
    this._startCraneAnim()
  }

  private _startCraneAnim() {
    if (this._animRaf) return
    const animate = () => {
      let allDone = true
      for (const crane of this._cranes) {
        const s = crane.state, speed = 0.08
        s.x += (s.targetX - s.x) * speed; s.y += (s.targetY - s.y) * speed; s.forkZ += (s.targetForkZ - s.forkZ) * speed
        crane.mastGroup.position.x = s.x; crane.carriageGroup.position.y = s.y; crane.forkGroup.position.z = s.forkZ
        if (Math.abs(s.targetX - s.x) > 0.1 || Math.abs(s.targetY - s.y) > 0.1 || Math.abs(s.targetForkZ - s.forkZ) > 0.1) { allDone = false }
        else { s.x = s.targetX; s.y = s.targetY; s.forkZ = s.targetForkZ; crane.mastGroup.position.x = s.x; crane.carriageGroup.position.y = s.y; crane.forkGroup.position.z = s.forkZ }
      }
      if (allDone) { this._animRaf = 0; return }
      this._animRaf = requestAnimationFrame(animate)
    }
    this._animRaf = requestAnimationFrame(animate)
  }

  onchange(after: Record<string, unknown>, before: Record<string, unknown>) {
    if ('data' in after) { this.updateCraneTargets(); this.update(); return }
    if ('lBays' in after || 'lLevels' in after || 'lDepth' in after || 'rBays' in after || 'rLevels' in after || 'rDepth' in after ||
        'cranes' in after || 'depth' in after || 'aisleRatio' in after ||
        'width' in after || 'height' in after || 'locationRule' in after || 'hideRackFrame' in after || 'fillStyle' in after || 'frontSide' in after) {
      this.update(); return
    }
    super.onchange(after, before)
  }

  dispose() {
    if (this._animRaf) { cancelAnimationFrame(this._animRaf); this._animRaf = 0 }
    this._cranes = []; this._cellMeshes = []
    super.dispose()
  }

  updateDimension() {}
  updateAlpha() {}
}
